import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import { EMPTY_HIERARCHY } from '@totem/components/services/threats/configuration/types';
import TicketAssignmentsContext from '@totem/components/ticketing/assignments/TicketAssignmentsContext';
import {
  DEFAULT_SEQUENCE,
  TicketAssignment,
  TicketAssignmentTestRequest,
  TicketAssignmentTestResult,
} from '@totem/components/ticketing/assignments/types';
import {
  CategorizationManagementResponse,
  CategorizationNode,
  OrganizationReference,
} from '@totem/components/ticketing/categorizationManagement/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import {
  TICKET_ASSIGNMENTS_ENDPOINT,
  TICKET_ENDPOINT,
} from '@totem/utilities/endpoints';

const EMPTY_ASSIGNMENT: TicketAssignment = {
  id: '',
  name: '',
  status: 'Active',
  sequence: DEFAULT_SEQUENCE,
  hierarchy: EMPTY_HIERARCHY,
  match: { type: '', category: '', subCategory: '', summaryContains: '' },
  assignees: [],
  notes: '',
  createdBy: '',
  createdAt: '',
  updatedBy: '',
  updatedAt: '',
};

type Props = {
  children: ReactNode;
};

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

const errorMessage = async (res: Response, fallback: string) => {
  try {
    const body = await res.json();
    return body?.developerMessage || fallback;
  } catch {
    return fallback;
  }
};

const TicketAssignmentsContainer = ({ children }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const [loading, setLoading] = useState(false);
  const [assignments, setAssignments] = useState<TicketAssignment[]>([]);
  const [categorization, setCategorization] = useState<CategorizationNode[]>(
    [],
  );
  const [organizations, setOrganizations] = useState<OrganizationReference[]>(
    [],
  );
  const [editingAssignment, setEditingAssignment] =
    useState<TicketAssignment | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  // Matches the backend (ticketing-library canManageTicketCategorization)
  const canEdit =
    permissions.internalIBOrg.hasPermissions(userProfile) &&
    permissions.ticketingAdministrator.hasPermissions(userProfile);

  const refresh = useCallback(() => {
    setLoading(true);

    fetch(TICKET_ASSIGNMENTS_ENDPOINT, { headers: authHeaders() })
      .then((res) => {
        if (!res.ok) throw new Error('Load failed');
        return res.json();
      })
      .then((records: TicketAssignment[]) => setAssignments(records ?? []))
      .catch(() =>
        notification.error({ message: 'Failed to load ticket assignments.' }),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    fetch(`${TICKET_ENDPOINT}/categorization`, { headers: authHeaders() })
      .then((res) => (res.ok ? res.json() : null))
      .then((result: CategorizationManagementResponse | null) => {
        if (result) {
          setCategorization(result.configuration?.data ?? []);

          setOrganizations(result.organizations ?? []);
        }
      })
      .catch(() => {});
  }, []);

  const openDrawer = (assignment: TicketAssignment) => {
    setEditingAssignment(assignment);

    setDrawerOpen(true);
  };

  const onAdd = () => openDrawer({ ...EMPTY_ASSIGNMENT });

  const onEdit = (assignment: TicketAssignment) =>
    openDrawer({ ...assignment });

  const onDuplicate = (assignment: TicketAssignment) =>
    openDrawer({
      ...assignment,
      id: '',
      name: `${assignment.name} (copy)`,
      status: 'Inactive',
      createdBy: '',
      createdAt: '',
      updatedBy: '',
      updatedAt: '',
    });

  const onDrawerClose = () => {
    setDrawerOpen(false);

    setEditingAssignment(null);
  };

  const onSave = (assignment: TicketAssignment) => {
    const isNew = !assignment.id;
    const url = isNew
      ? TICKET_ASSIGNMENTS_ENDPOINT
      : `${TICKET_ASSIGNMENTS_ENDPOINT}/${assignment.id}`;

    setSaving(true);

    fetch(url, {
      method: isNew ? 'POST' : 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(assignment),
    })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(
            await errorMessage(res, 'Failed to save ticket assignment.'),
          );
        }
        notification.success({
          message: `Ticket assignment ${isNew ? 'created' : 'updated'}.`,
        });

        onDrawerClose();

        refresh();
      })
      .catch((err: Error) => notification.error({ message: err.message }))
      .finally(() => setSaving(false));
  };

  const onDelete = (id: string) => {
    fetch(`${TICKET_ASSIGNMENTS_ENDPOINT}/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Delete failed');
        notification.success({ message: 'Ticket assignment deleted.' });

        refresh();
      })
      .catch(() =>
        notification.error({ message: 'Failed to delete ticket assignment.' }),
      );
  };

  const runTest = (request: TicketAssignmentTestRequest) =>
    fetch(`${TICKET_ASSIGNMENTS_ENDPOINT}/test`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(request),
    }).then(async (res) => {
      if (!res.ok) {
        throw new Error(await errorMessage(res, 'Test failed.'));
      }
      return ((await res.json()) ?? []) as TicketAssignmentTestResult[];
    });

  return (
    <TicketAssignmentsContext.Provider
      value={{
        loading,
        assignments,
        categorization,
        organizations,
        canEdit,

        editingAssignment,
        drawerOpen,
        saving,
        onAdd,
        onEdit,
        onDuplicate,
        onDrawerClose,
        onSave,
        onDelete,

        testOpen,
        onTestOpen: () => setTestOpen(true),
        onTestClose: () => setTestOpen(false),
        runTest,
      }}
    >
      {children}
    </TicketAssignmentsContext.Provider>
  );
};

export default TicketAssignmentsContainer;
